/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";
import { I18nMessage } from "@i18n/next";
import type { FlaggedRecord } from "@packages/sdk";
import { getFullName, useDate } from "@packages/sdk";
import type { ThemeType } from "@packages/ui";
import {
  Avatar,
  FLAG_RECORD_REASON_OPTIONS,
  radius,
  Skeleton,
  spacing,
  stroke,
  Text,
} from "@packages/ui";
import type { HTMLAttributes } from "react";

const baseStyles: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.s,
};

const commentStyles: Interpolation<ThemeType> = (theme: ThemeType) => ({
  color: theme.semanticColors.neutralsHigher,
});

const headerStyles: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.s,
};

const headerTextStyles: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.t,
};

const headerTextTimeStyles: Interpolation<ThemeType> = (theme: ThemeType) => ({
  color: theme.semanticColors.neutralsMedium,
});

const reasonStyles: Interpolation<ThemeType> = (theme: ThemeType) => ({
  borderColor: theme.semanticColors.neutralsLowest,
  borderRadius: radius.s,
  borderStyle: "solid",
  borderWidth: stroke.light,
  display: "flex",
  flexDirection: "column",
  gap: spacing.xxs,
  padding: spacing.m,
});

const reasonDescriptionStyles: Interpolation<ThemeType> = (
  theme: ThemeType,
) => ({
  color: theme.semanticColors.neutralsMedium,
});

const reasonTitleStyles: Interpolation<ThemeType> = (theme: ThemeType) => ({
  color: theme.semanticColors.neutralsHigher,
});

const loadingBaseStyles: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.ms,
};

const loadingCommentStyles: Interpolation = {
  height: 60,
  width: spacing.full,
};

const loadingHeaderStyles: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.s,
};

const loadingHeaderTextStyles: Interpolation = {
  display: "flex",
  flex: 1,
  flexDirection: "column",
  gap: spacing.t,
};

const loadingHeaderTextNameStyles: Interpolation = {
  height: 18,
  width: "75%",
};

const loadingHeaderTextTimeStyles: Interpolation = {
  height: spacing.m,
  width: "25%",
};

const loadingReasonStyles: Interpolation = {
  height: 72,
  width: spacing.full,
};

type ReportedCommentProps = Omit<HTMLAttributes<HTMLDivElement>, "children"> & {
  data: FlaggedRecord;
  loading?: boolean;
};

export const ReportedComment = ({
  data,
  loading,
  ...props
}: ReportedCommentProps) => {
  const { getDateFormatDifferenceNow } = useDate();

  const reason = FLAG_RECORD_REASON_OPTIONS.find(
    (reason) => reason.value === data?.reason,
  );

  if (!data || loading)
    return (
      <div css={loadingBaseStyles} {...props}>
        <div css={loadingHeaderStyles}>
          <Avatar loading size={40} />
          <div css={loadingHeaderTextStyles}>
            <Skeleton css={loadingHeaderTextNameStyles} />
            <Skeleton css={loadingHeaderTextTimeStyles} />
          </div>
        </div>
        <Skeleton css={loadingReasonStyles} />
        <Skeleton css={loadingCommentStyles} />
      </div>
    );

  return (
    <div css={baseStyles} {...props}>
      <div css={headerStyles}>
        <Avatar imgSrc={data?.reported_by.image_url} size={40} />
        <div css={headerTextStyles}>
          <Text size="xl" type="detail">
            <strong>
              {getFullName({
                firstName: data?.reported_by.name,
                lastName: data?.reported_by.last_name,
              })}
            </strong>
          </Text>
          <Text size="l" css={headerTextTimeStyles} type="detail">
            {getDateFormatDifferenceNow({ date: new Date(data?.created_at) })}
          </Text>
        </div>
      </div>
      <div css={reasonStyles}>
        <Text as="h4" size="s" css={reasonTitleStyles} type="title">
          <I18nMessage message={reason.labelLokaliseKey} />
        </Text>
        <Text as="p" size="s" css={reasonDescriptionStyles} type="body">
          <I18nMessage message={reason.sublabelLokaliseKey} />
        </Text>
      </div>
      <Text as="p" size="m" css={commentStyles} type="body">
        {data?.comment}
      </Text>
    </div>
  );
};

ReportedComment.displayName = "ReportedComment";
